import { useEffect } from 'react';

const KOLLUS_IFRAME_SRC_PATTERN = /v\.kr\.kollus\.com/;
const DEFAULT_VOLUME = 50;

export function useKollusVolumeEnforcer({ defaultVolume = DEFAULT_VOLUME } = {}) {
  useEffect(() => {
    const attachedIframes = new WeakSet();

    const applyVolume = (controller) => {
      try {
        controller.set_volume?.(defaultVolume);
      } catch (e) {
        console.warn('[useKollusVolumeEnforcer] set_volume failed:', e);
      }
    };

    const forceVolume = (iframe) => {
      const ControllerClient = window.WebPlayerControllerClient || window.VgControllerClient;

      if (!ControllerClient || !iframe.contentWindow) return;

      try {
        const controller = new ControllerClient({
          target_window: iframe.contentWindow,
        });

        // 플레이어가 준비된 시점에 볼륨 강제 적용
        controller.on?.('ready', () => applyVolume(controller));
        // ready 이전에 재생이 시작되는 케이스에 대비해 play 시점에도 한 번 더 적용
        controller.on?.('play', () => applyVolume(controller));
      } catch (e) {
        console.warn('[useKollusVolumeEnforcer] controller init failed:', e);
      }
    };

    const shouldSkip = (iframe) => {
      return iframe.dataset?.skipGlobalVolume !== undefined;
    };

    const handleIframe = (iframe) => {
      if (!iframe || attachedIframes.has(iframe)) return;
      if (!KOLLUS_IFRAME_SRC_PATTERN.test(iframe.src || '')) return;
      if (shouldSkip(iframe)) return;

      attachedIframes.add(iframe);

      iframe.addEventListener('load', () => forceVolume(iframe));
      // 이미 로드가 끝난 상태일 수 있으니 즉시 한 번 시도
      forceVolume(iframe);
    };

    // 최초 진입 시 이미 존재하는 iframe들 처리
    document.querySelectorAll('iframe').forEach(handleIframe);

    // 이후 라우트 이동 등으로 새로 생기는 iframe 감시
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          if (node.tagName === 'IFRAME') {
            handleIframe(node);
          }
          node.querySelectorAll?.('iframe').forEach(handleIframe);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [defaultVolume]);
}

export default useKollusVolumeEnforcer;
